/**
 * Integrations Settings Page
 *
 * Manage API keys and webhooks for programmatic access to the platform.
 */

import { useEffect, useState } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { generateId, formatDate, copyToClipboard } from '@/utils';
import { WEBHOOK_EVENTS, WEBHOOK_EVENT_CATEGORIES, getWebhookEventsByCategory } from '@/constants';
import type { Webhook, WebhookEvent } from '@/types';
import {
  Key,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  RefreshCw,
  AlertTriangle,
  Webhook as WebhookIcon,
  Globe,
  CheckCircle2,
  XCircle,
  Settings2,
  Play,
  Pause,
  MoreVertical,
  ExternalLink,
  Clock,
  Zap,
} from 'lucide-react';

// ============================================================================
// API Key Types
// ============================================================================

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  fullKey?: string;
  createdAt: string;
  lastUsed: string | null;
  status: 'active' | 'revoked';
  scopes: string[];
}

const AVAILABLE_SCOPES = [
  { id: 'read:phones', label: 'Read phone numbers', description: 'View phone number data and reputation' },
  { id: 'write:phones', label: 'Write phone numbers', description: 'Add and update phone numbers' },
  { id: 'read:lookups', label: 'Read lookups', description: 'View lookup history and results' },
  { id: 'write:lookups', label: 'Perform lookups', description: 'Trigger reputation lookups' },
  { id: 'read:analytics', label: 'Read analytics', description: 'View dashboard and reports' },
  { id: 'webhooks:manage', label: 'Manage webhooks', description: 'Create and configure webhooks' },
];

function generateApiKey(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let key = 'rc_';
  for (let i = 0; i < 32; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
}

function generateWebhookSecret(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let secret = 'whsec_';
  for (let i = 0; i < 32; i++) {
    secret += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return secret;
}

// ============================================================================
// Main Component
// ============================================================================

export function IntegrationsPage() {
  const { success: showSuccess, error: showError, info: showInfo } = useToast();
  const company = useAuthStore((state) => state.company);

  // API Keys State
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isCreateKeyModalOpen, setIsCreateKeyModalOpen] = useState(false);
  const [isDeleteKeyModalOpen, setIsDeleteKeyModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['read:phones', 'read:lookups']);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<ApiKey | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  // Webhooks State
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isCreateWebhookModalOpen, setIsCreateWebhookModalOpen] = useState(false);
  const [isEditWebhookModalOpen, setIsEditWebhookModalOpen] = useState(false);
  const [isDeleteWebhookModalOpen, setIsDeleteWebhookModalOpen] = useState(false);
  const [webhookToEdit, setWebhookToEdit] = useState<Webhook | null>(null);
  const [webhookToDelete, setWebhookToDelete] = useState<Webhook | null>(null);
  const [isTestingWebhook, setIsTestingWebhook] = useState<string | null>(null);

  // Webhook Form State
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: [] as WebhookEvent[],
  });
  const [webhookFormErrors, setWebhookFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!company?.id) return;

    Logger.info(LogCategory.UI, 'IntegrationsPage mounted', 'IntegrationsPage');

    async function fetchData() {
      try {
        // Fetch API keys from Supabase
        const { data: dbKeys, error: keysError } = await supabase
          .from('api_keys')
          .select('*')
          .eq('company_id', company!.id)
          .order('created_at', { ascending: false });

        if (keysError) throw keysError;

        const keys: ApiKey[] = (dbKeys || []).map((k: any) => ({
          id: k.id,
          name: k.name,
          keyPrefix: k.key_prefix,
          createdAt: k.created_at,
          lastUsed: k.last_used_at,
          status: k.is_active ? 'active' : 'revoked',
          scopes: k.permissions || [],
        }));
        setApiKeys(keys);

        // Fetch webhooks from Supabase
        const { data: dbWebhooks, error: webhooksError } = await supabase
          .from('webhooks')
          .select('*')
          .eq('company_id', company!.id)
          .order('created_at', { ascending: false });

        if (webhooksError) throw webhooksError;

        const whs: Webhook[] = (dbWebhooks || []).map((w: any) => ({
          id: w.id,
          name: w.name,
          url: w.url,
          events: w.events || [],
          status: w.status,
          secret: w.secret,
          createdAt: w.created_at,
          updatedAt: w.updated_at,
          lastTriggeredAt: w.last_triggered_at,
          lastResponseStatus: w.last_response_status,
          failureCount: w.failure_count || 0,
        }));
        setWebhooks(whs);
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to fetch integrations data', 'IntegrationsPage', undefined, err);
      }
    }

    fetchData();
  }, [company?.id]);

  // ============================================================================
  // API Key Handlers
  // ============================================================================

  const handleCreateKey = async () => {
    Logger.info(LogCategory.API, 'Creating new API key', 'IntegrationsPage', {
      name: newKeyName,
      scopes: selectedScopes,
    });

    if (!newKeyName.trim()) {
      showError('Name required', 'Please enter a name for your API key.');
      return;
    }

    if (selectedScopes.length === 0) {
      showError('Scopes required', 'Please select at least one scope.');
      return;
    }

    const fullKey = generateApiKey();
    const keyPrefix = fullKey.substring(0, 10) + '...';

    // Hash the key for storage (use a simple hash since we show the key once)
    const encoder = new TextEncoder();
    const data = encoder.encode(fullKey);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const keyHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: dbKey, error } = await supabase
        .from('api_keys')
        .insert({
          company_id: company!.id,
          created_by_user_id: user?.id,
          name: newKeyName.trim(),
          key_hash: keyHash,
          key_prefix: keyPrefix,
          permissions: selectedScopes,
        } as any)
        .select()
        .single();

      if (error) throw error;

      const newKey: ApiKey = {
        id: (dbKey as any).id,
        name: (dbKey as any).name,
        keyPrefix: (dbKey as any).key_prefix,
        fullKey,
        createdAt: (dbKey as any).created_at,
        lastUsed: null,
        status: 'active',
        scopes: (dbKey as any).permissions || [],
      };

      setApiKeys((prev) => [newKey, ...prev]);
      setNewlyCreatedKey(newKey);
      setIsCreateKeyModalOpen(false);
      setNewKeyName('');
      setSelectedScopes(['read:phones', 'read:lookups']);

      Logger.info(LogCategory.API, 'API key created successfully', 'IntegrationsPage', {
        keyId: newKey.id,
      });
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to create API key', 'IntegrationsPage', undefined, err);
      showError('Failed to create key', 'An error occurred while creating the API key.');
    }
  };

  const handleCopyKey = async (key: string) => {
    Logger.debug(LogCategory.UI, 'Copying API key to clipboard', 'IntegrationsPage');
    const success = await copyToClipboard(key);
    if (success) {
      showSuccess('Copied!', 'API key copied to clipboard.');
    } else {
      showError('Copy failed', 'Could not copy to clipboard.');
    }
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;

    Logger.info(LogCategory.API, 'Deleting API key', 'IntegrationsPage', {
      keyId: keyToDelete.id,
      keyName: keyToDelete.name,
    });

    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', keyToDelete.id);

      if (error) throw error;

      setApiKeys((prev) => prev.filter((k) => k.id !== keyToDelete.id));
      setIsDeleteKeyModalOpen(false);
      setKeyToDelete(null);
      showSuccess('Key deleted', 'The API key has been permanently deleted.');
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to delete API key', 'IntegrationsPage', undefined, err);
      showError('Failed to delete', 'An error occurred while deleting the API key.');
    }
  };

  const handleRevokeKey = async (key: ApiKey) => {
    Logger.info(LogCategory.API, 'Revoking API key', 'IntegrationsPage', {
      keyId: key.id,
      keyName: key.name,
    });

    try {
      const { error } = await (supabase
        .from('api_keys') as any)
        .update({ is_active: false })
        .eq('id', key.id);

      if (error) throw error;

      setApiKeys((prev) =>
        prev.map((k) =>
          k.id === key.id ? { ...k, status: 'revoked' as const } : k
        )
      );
      showInfo('Key revoked', 'The API key has been revoked and can no longer be used.');
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to revoke API key', 'IntegrationsPage', undefined, err);
      showError('Failed to revoke', 'An error occurred while revoking the API key.');
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const handleScopeToggle = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  // ============================================================================
  // Webhook Handlers
  // ============================================================================

  const validateWebhookForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!webhookForm.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!webhookForm.url.trim()) {
      errors.url = 'URL is required';
    } else {
      try {
        new URL(webhookForm.url);
        if (!webhookForm.url.startsWith('https://')) {
          errors.url = 'URL must use HTTPS';
        }
      } catch {
        errors.url = 'Please enter a valid URL';
      }
    }

    if (webhookForm.events.length === 0) {
      errors.events = 'Select at least one event';
    }

    setWebhookFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateWebhook = async () => {
    Logger.info(LogCategory.API, 'Creating new webhook', 'IntegrationsPage', {
      name: webhookForm.name,
      events: webhookForm.events,
    });

    if (!validateWebhookForm()) {
      return;
    }

    const secret = generateWebhookSecret();

    try {
      const { data: dbWebhook, error } = await supabase
        .from('webhooks')
        .insert({
          company_id: company!.id,
          name: webhookForm.name.trim(),
          url: webhookForm.url.trim(),
          secret,
          events: webhookForm.events,
          status: 'active',
        } as any)
        .select()
        .single();

      if (error) throw error;

      const newWebhook: Webhook = {
        id: (dbWebhook as any).id,
        name: (dbWebhook as any).name,
        url: (dbWebhook as any).url,
        events: (dbWebhook as any).events || [],
        status: (dbWebhook as any).status,
        secret: (dbWebhook as any).secret,
        createdAt: (dbWebhook as any).created_at,
        updatedAt: (dbWebhook as any).updated_at,
        lastTriggeredAt: null,
        lastResponseStatus: null,
        failureCount: 0,
      };

      setWebhooks((prev) => [newWebhook, ...prev]);
      setIsCreateWebhookModalOpen(false);
      resetWebhookForm();
      showSuccess('Webhook created', `${newWebhook.name} has been created and is now active.`);
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to create webhook', 'IntegrationsPage', undefined, err);
      showError('Failed to create webhook', 'An error occurred while creating the webhook.');
    }
  };

  const handleUpdateWebhook = async () => {
    if (!webhookToEdit) return;

    Logger.info(LogCategory.API, 'Updating webhook', 'IntegrationsPage', {
      webhookId: webhookToEdit.id,
    });

    if (!validateWebhookForm()) {
      return;
    }

    try {
      const { data: dbWebhook, error } = await (supabase
        .from('webhooks') as any)
        .update({
          name: webhookForm.name.trim(),
          url: webhookForm.url.trim(),
          events: webhookForm.events,
        })
        .eq('id', webhookToEdit.id)
        .select()
        .single();

      if (error) throw error;

      setWebhooks((prev) =>
        prev.map((w) =>
          w.id === webhookToEdit.id
            ? {
                ...w,
                name: (dbWebhook as any).name,
                url: (dbWebhook as any).url,
                events: (dbWebhook as any).events || [],
                updatedAt: (dbWebhook as any).updated_at,
              }
            : w
        )
      );

      setIsEditWebhookModalOpen(false);
      setWebhookToEdit(null);
      resetWebhookForm();
      showSuccess('Webhook updated', 'Your changes have been saved.');
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to update webhook', 'IntegrationsPage', undefined, err);
      showError('Failed to update', 'An error occurred while updating the webhook.');
    }
  };

  const handleDeleteWebhook = async () => {
    if (!webhookToDelete) return;

    Logger.info(LogCategory.API, 'Deleting webhook', 'IntegrationsPage', {
      webhookId: webhookToDelete.id,
    });

    try {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', webhookToDelete.id);

      if (error) throw error;

      setWebhooks((prev) => prev.filter((w) => w.id !== webhookToDelete.id));
      setIsDeleteWebhookModalOpen(false);
      setWebhookToDelete(null);
      showSuccess('Webhook deleted', 'The webhook has been permanently deleted.');
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to delete webhook', 'IntegrationsPage', undefined, err);
      showError('Failed to delete', 'An error occurred while deleting the webhook.');
    }
  };

  const handleToggleWebhookStatus = async (webhook: Webhook) => {
    const newStatus = webhook.status === 'active' ? 'inactive' : 'active';

    Logger.info(LogCategory.API, `${newStatus === 'active' ? 'Enabling' : 'Disabling'} webhook`, 'IntegrationsPage', {
      webhookId: webhook.id,
    });

    try {
      const { error } = await (supabase
        .from('webhooks') as any)
        .update({ status: newStatus })
        .eq('id', webhook.id);

      if (error) throw error;

      setWebhooks((prev) =>
        prev.map((w) =>
          w.id === webhook.id
            ? { ...w, status: newStatus, updatedAt: new Date().toISOString() }
            : w
        )
      );

      showInfo(
        `Webhook ${newStatus}`,
        newStatus === 'active'
          ? 'The webhook will now receive events.'
          : 'The webhook has been paused and will not receive events.'
      );
    } catch (err) {
      Logger.error(LogCategory.API, 'Failed to toggle webhook status', 'IntegrationsPage', undefined, err);
      showError('Failed to update', 'An error occurred while updating the webhook.');
    }
  };

  const handleTestWebhook = async (webhook: Webhook) => {
    Logger.info(LogCategory.API, 'Testing webhook', 'IntegrationsPage', {
      webhookId: webhook.id,
    });

    setIsTestingWebhook(webhook.id);

    try {
      // Send a test payload to the webhook URL
      const testPayload = {
        event: 'test.ping',
        timestamp: new Date().toISOString(),
        data: { message: 'This is a test event from RepuCall' },
      };

      const response = await fetch(webhook.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testPayload),
      });

      if (response.ok) {
        showSuccess('Test sent', 'A test event was sent to your webhook endpoint.');
      } else {
        showError('Test failed', `Endpoint returned status ${response.status}.`);
      }
    } catch {
      showError('Test failed', 'Could not reach the webhook endpoint.');
    } finally {
      setIsTestingWebhook(null);
    }
  };

  const handleCopySecret = async (secret: string) => {
    const success = await copyToClipboard(secret);
    if (success) {
      showSuccess('Copied!', 'Webhook secret copied to clipboard.');
    } else {
      showError('Copy failed', 'Could not copy to clipboard.');
    }
  };

  const openEditWebhookModal = (webhook: Webhook) => {
    setWebhookToEdit(webhook);
    setWebhookForm({
      name: webhook.name,
      url: webhook.url,
      events: [...webhook.events],
    });
    setWebhookFormErrors({});
    setIsEditWebhookModalOpen(true);
  };

  const resetWebhookForm = () => {
    setWebhookForm({ name: '', url: '', events: [] });
    setWebhookFormErrors({});
  };

  const handleWebhookEventToggle = (eventId: WebhookEvent) => {
    setWebhookForm((prev) => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter((e) => e !== eventId)
        : [...prev.events, eventId],
    }));
    if (webhookFormErrors.events) {
      setWebhookFormErrors((prev) => ({ ...prev, events: '' }));
    }
  };

  const activeKeys = apiKeys.filter((k) => k.status === 'active');
  const activeWebhooks = webhooks.filter((w) => w.status === 'active');
  const eventsByCategory = getWebhookEventsByCategory();

  return (
    <AppLayout>
      <PageHeader
        title="Integrations"
        description="Manage API keys and webhooks for your integrations"
      />

      <div className="max-w-5xl space-y-8">
        {/* Newly Created Key Alert */}
        {newlyCreatedKey && newlyCreatedKey.fullKey && (
          <Card variant="elevated" className="border-warning-300 bg-warning-50">
            <CardContent>
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-full bg-warning-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-5 w-5 text-warning-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-warning-900">
                    Save your API key now!
                  </h3>
                  <p className="text-sm text-warning-700 mt-1">
                    This is the only time you'll see this key. Make sure to copy it and store it securely.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white border border-warning-200 rounded text-sm font-mono">
                      {newlyCreatedKey.fullKey}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyKey(newlyCreatedKey.fullKey!)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => setNewlyCreatedKey(null)}
                  >
                    I've saved my key
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ================================================================== */}
        {/* API Keys Section */}
        {/* ================================================================== */}

        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">API Keys</h2>
              <p className="text-sm text-slate-500">
                Create and manage API keys for programmatic access
              </p>
            </div>
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setIsCreateKeyModalOpen(true)}
            >
              Create API Key
            </Button>
          </div>

          <Card>
            <CardContent padding="none">
              {apiKeys.length === 0 ? (
                <div className="p-8 text-center">
                  <Key className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                  <p className="text-slate-600">No API keys yet.</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Create your first API key to get started with the API.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {apiKeys.map((key) => (
                    <div key={key.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Key className="h-5 w-5 text-slate-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{key.name}</p>
                              <Badge
                                variant={key.status === 'active' ? 'success' : 'danger'}
                                size="sm"
                              >
                                {key.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="text-sm font-mono text-slate-500">
                                {visibleKeys.has(key.id) && key.fullKey
                                  ? key.fullKey
                                  : key.keyPrefix}
                              </code>
                              {key.fullKey && (
                                <button
                                  onClick={() => toggleKeyVisibility(key.id)}
                                  className="text-slate-400 hover:text-slate-600"
                                >
                                  {visibleKeys.has(key.id) ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                              <span>Created {formatDate(key.createdAt, 'short')}</span>
                              <span>•</span>
                              <span>
                                {key.lastUsed
                                  ? `Last used ${formatDate(key.lastUsed, 'relative')}`
                                  : 'Never used'}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {key.scopes.slice(0, 3).map((scope) => (
                                <Badge key={scope} variant="default" size="sm">
                                  {scope}
                                </Badge>
                              ))}
                              {key.scopes.length > 3 && (
                                <Badge variant="default" size="sm">
                                  +{key.scopes.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {key.status === 'active' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCopyKey(key.fullKey || key.keyPrefix)}
                                title="Copy key"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokeKey(key)}
                                title="Revoke key"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setKeyToDelete(key);
                              setIsDeleteKeyModalOpen(true);
                            }}
                            title="Delete key"
                          >
                            <Trash2 className="h-4 w-4 text-danger-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ================================================================== */}
        {/* Webhooks Section */}
        {/* ================================================================== */}

        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Webhooks</h2>
              <p className="text-sm text-slate-500">
                Receive real-time notifications when events happen in your account
              </p>
            </div>
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                resetWebhookForm();
                setIsCreateWebhookModalOpen(true);
              }}
            >
              Add Webhook
            </Button>
          </div>

          {/* Webhook Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                <WebhookIcon className="h-4 w-4" />
                Total Webhooks
              </div>
              <p className="text-2xl font-bold text-slate-900">{webhooks.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-success-600 text-sm mb-1">
                <CheckCircle2 className="h-4 w-4" />
                Active
              </div>
              <p className="text-2xl font-bold text-slate-900">{activeWebhooks.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                <Zap className="h-4 w-4" />
                Events Subscribed
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {new Set(webhooks.flatMap((w) => w.events)).size}
              </p>
            </div>
          </div>

          <Card>
            <CardContent padding="none">
              {webhooks.length === 0 ? (
                <div className="p-8 text-center">
                  <WebhookIcon className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                  <p className="text-slate-600">No webhooks configured.</p>
                  <p className="text-sm text-slate-500 mt-1">
                    Add a webhook to receive real-time event notifications.
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => {
                      resetWebhookForm();
                      setIsCreateWebhookModalOpen(true);
                    }}
                  >
                    Add Your First Webhook
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {webhooks.map((webhook) => (
                    <div key={webhook.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            webhook.status === 'active'
                              ? 'bg-success-100'
                              : 'bg-slate-100'
                          }`}>
                            <WebhookIcon className={`h-5 w-5 ${
                              webhook.status === 'active'
                                ? 'text-success-600'
                                : 'text-slate-400'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{webhook.name}</p>
                              <Badge
                                variant={webhook.status === 'active' ? 'success' : 'default'}
                                size="sm"
                              >
                                {webhook.status}
                              </Badge>
                              {webhook.failureCount > 0 && (
                                <Badge variant="danger" size="sm">
                                  {webhook.failureCount} failures
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Globe className="h-3.5 w-3.5 text-slate-400" />
                              <code className="text-sm font-mono text-slate-500 truncate">
                                {webhook.url}
                              </code>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {webhook.events.map((event) => {
                                const eventInfo = WEBHOOK_EVENTS.find((e) => e.id === event);
                                return (
                                  <Badge key={event} variant="default" size="sm">
                                    {eventInfo?.name || event}
                                  </Badge>
                                );
                              })}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {webhook.lastTriggeredAt
                                  ? `Last triggered ${formatDate(webhook.lastTriggeredAt, 'relative')}`
                                  : 'Never triggered'}
                              </span>
                              {webhook.lastResponseStatus && (
                                <span className={`flex items-center gap-1 ${
                                  webhook.lastResponseStatus >= 200 && webhook.lastResponseStatus < 300
                                    ? 'text-success-600'
                                    : 'text-danger-600'
                                }`}>
                                  {webhook.lastResponseStatus >= 200 && webhook.lastResponseStatus < 300 ? (
                                    <CheckCircle2 className="h-3 w-3" />
                                  ) : (
                                    <XCircle className="h-3 w-3" />
                                  )}
                                  Status {webhook.lastResponseStatus}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTestWebhook(webhook)}
                            disabled={webhook.status === 'inactive' || isTestingWebhook === webhook.id}
                            isLoading={isTestingWebhook === webhook.id}
                            title="Send test event"
                          >
                            <Zap className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleWebhookStatus(webhook)}
                            title={webhook.status === 'active' ? 'Pause webhook' : 'Enable webhook'}
                          >
                            {webhook.status === 'active' ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditWebhookModal(webhook)}
                            title="Edit webhook"
                          >
                            <Settings2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopySecret(webhook.secret)}
                            title="Copy signing secret"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setWebhookToDelete(webhook);
                              setIsDeleteWebhookModalOpen(true);
                            }}
                            title="Delete webhook"
                          >
                            <Trash2 className="h-4 w-4 text-danger-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Documentation Link */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-600">
            <strong>Need help?</strong> Check out our{' '}
            <a href="#" className="text-primary-600 hover:underline inline-flex items-center gap-1">
              API documentation
              <ExternalLink className="h-3 w-3" />
            </a>{' '}
            for integration guides, webhook payload examples, and code samples.
          </p>
        </div>
      </div>

      {/* ================================================================== */}
      {/* Create API Key Modal */}
      {/* ================================================================== */}

      <Modal
        isOpen={isCreateKeyModalOpen}
        onClose={() => setIsCreateKeyModalOpen(false)}
        title="Create API Key"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Key Name"
            placeholder="e.g., Production API Key"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            helperText="A descriptive name to identify this key"
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Permissions
            </label>
            <div className="space-y-2">
              {AVAILABLE_SCOPES.map((scope) => (
                <label
                  key={scope.id}
                  className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg hover:border-slate-300 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedScopes.includes(scope.id)}
                    onChange={() => handleScopeToggle(scope.id)}
                    className="mt-0.5 h-4 w-4 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
                  />
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{scope.label}</p>
                    <p className="text-xs text-slate-500">{scope.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setIsCreateKeyModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKey}>Create Key</Button>
          </div>
        </div>
      </Modal>

      {/* Delete API Key Modal */}
      <Modal
        isOpen={isDeleteKeyModalOpen}
        onClose={() => setIsDeleteKeyModalOpen(false)}
        title="Delete API Key"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Are you sure you want to delete the API key "{keyToDelete?.name}"?
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsDeleteKeyModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteKey}>
              Delete Key
            </Button>
          </div>
        </div>
      </Modal>

      {/* ================================================================== */}
      {/* Create/Edit Webhook Modal */}
      {/* ================================================================== */}

      <Modal
        isOpen={isCreateWebhookModalOpen || isEditWebhookModalOpen}
        onClose={() => {
          setIsCreateWebhookModalOpen(false);
          setIsEditWebhookModalOpen(false);
          setWebhookToEdit(null);
          resetWebhookForm();
        }}
        title={isEditWebhookModalOpen ? 'Edit Webhook' : 'Add Webhook'}
        size="lg"
      >
        <div className="space-y-6">
          <Input
            label="Webhook Name"
            placeholder="e.g., Slack Notifications"
            value={webhookForm.name}
            onChange={(e) => {
              setWebhookForm((prev) => ({ ...prev, name: e.target.value }));
              if (webhookFormErrors.name) {
                setWebhookFormErrors((prev) => ({ ...prev, name: '' }));
              }
            }}
            error={webhookFormErrors.name}
            helperText="A friendly name to identify this webhook"
          />

          <Input
            label="Endpoint URL"
            placeholder="https://your-server.com/webhook"
            value={webhookForm.url}
            onChange={(e) => {
              setWebhookForm((prev) => ({ ...prev, url: e.target.value }));
              if (webhookFormErrors.url) {
                setWebhookFormErrors((prev) => ({ ...prev, url: '' }));
              }
            }}
            error={webhookFormErrors.url}
            helperText="The HTTPS URL where we'll send webhook events"
            leftIcon={<Globe className="h-4 w-4" />}
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Events to Subscribe
            </label>
            {webhookFormErrors.events && (
              <p className="text-sm text-danger-600 mb-2">{webhookFormErrors.events}</p>
            )}
            <div className="space-y-4">
              {Object.entries(eventsByCategory).map(([category, events]) => {
                const categoryInfo = WEBHOOK_EVENT_CATEGORIES[category as keyof typeof WEBHOOK_EVENT_CATEGORIES];
                return (
                  <div key={category} className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                      <p className="font-medium text-slate-700 text-sm">{categoryInfo.name}</p>
                    </div>
                    <div className="p-2 space-y-1">
                      {events.map((event) => (
                        <label
                          key={event.id}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={webhookForm.events.includes(event.id)}
                            onChange={() => handleWebhookEventToggle(event.id)}
                            className="mt-0.5 h-4 w-4 text-primary-600 rounded border-slate-300 focus:ring-primary-500"
                          />
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{event.name}</p>
                            <p className="text-xs text-slate-500">{event.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {isEditWebhookModalOpen && webhookToEdit && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-2">Signing Secret</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded text-sm font-mono text-slate-600">
                  {webhookToEdit.secret}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopySecret(webhookToEdit.secret)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Use this secret to verify webhook signatures in your application.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateWebhookModalOpen(false);
                setIsEditWebhookModalOpen(false);
                setWebhookToEdit(null);
                resetWebhookForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={isEditWebhookModalOpen ? handleUpdateWebhook : handleCreateWebhook}
            >
              {isEditWebhookModalOpen ? 'Save Changes' : 'Create Webhook'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Webhook Modal */}
      <Modal
        isOpen={isDeleteWebhookModalOpen}
        onClose={() => setIsDeleteWebhookModalOpen(false)}
        title="Delete Webhook"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Are you sure you want to delete the webhook "{webhookToDelete?.name}"?
            This action cannot be undone and you will stop receiving events at this endpoint.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsDeleteWebhookModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteWebhook}>
              Delete Webhook
            </Button>
          </div>
        </div>
      </Modal>
    </AppLayout>
  );
}
